import { useEffect, useState } from "react";
import { Mail, RefreshCw, FileDown, TrendingUp } from "lucide-react";
import api from "../lib/api";

const FOLLOWUP_SUBJECT = "Following up on RealtyVault Manager";
const FOLLOWUP_BODY = (prospect) =>
  `Hi ${prospect ? `${prospect} team` : "team"},\n\n` +
  `Following up on the RealtyVault Manager flyer I sent earlier — happy to walk you through a 15-min demo of the AI lease generator, multi-LLC workspaces, and one-click Lender Package.\n\n` +
  `What's a good time this week?\n\n` +
  `— sent from RealtyVault Manager`;

const fmtRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  return `${day}d ago`;
};

/**
 * Top-prospects-by-flyer-download leaderboard for the Super Admin Overview.
 *
 * Read from POST-flyer-download tracking (see /api/admin/flyer-downloads/
 * leaderboard). Surfaces who's been receiving the most outreach in the last
 * 7 days and gives a one-click "Send follow-up email" mailto: link per row.
 *
 * Self-refreshes when an admin clicks the manual refresh icon — also
 * automatically refreshes when the user re-focuses the page (tab switch).
 *
 * Hidden when there are zero downloads in the window (don't surface an
 * empty card on day-zero installs).
 */
export default function FlyerLeaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/flyer-downloads/leaderboard?days=7&limit=5")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  // Hidden when zero downloads (don't pollute day-zero overview).
  if (!loading && (!data || data.total === 0)) return null;

  return (
    <div
      className="mb-8 bg-white border border-slate-200 rounded-sm p-5"
      data-testid="flyer-leaderboard"
    >
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-sm bg-emerald-600 text-white flex items-center justify-center shrink-0">
            <TrendingUp className="w-5 h-5" />
          </div>
          <div>
            <div className="overline text-emerald-700">Top of funnel</div>
            <h2
              className="text-lg font-bold tracking-tight text-slate-900"
              style={{ fontFamily: "Outfit" }}
            >
              Top prospects · last {data?.window_days || 7} days
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {data?.total || 0} flyer download{data?.total === 1 ? "" : "s"}
              {data?.generic_count > 0 && (
                <span> · {data.generic_count} generic (no prospect)</span>
              )}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="text-slate-400 hover:text-slate-700 p-1 disabled:opacity-50"
          title="Refresh"
          data-testid="flyer-leaderboard-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {data?.top?.length > 0 ? (
        <ul className="divide-y divide-slate-100">
          {data.top.map((row, idx) => {
            const mailto = `mailto:?subject=${encodeURIComponent(
              FOLLOWUP_SUBJECT
            )}&body=${encodeURIComponent(FOLLOWUP_BODY(row.prospect))}`;
            return (
              <li
                key={row.prospect}
                className="flex items-center justify-between py-3 gap-3"
                data-testid={`flyer-leaderboard-row-${idx}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span
                    className="inline-flex items-center justify-center w-7 h-7 rounded-sm bg-slate-900 text-white text-xs font-bold shrink-0"
                    data-testid={`flyer-leaderboard-rank-${idx}`}
                  >
                    #{idx + 1}
                  </span>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">
                      {row.prospect}
                    </div>
                    <div className="text-xs text-slate-500">
                      <span className="font-mono font-semibold text-slate-700">
                        {row.count}
                      </span>{" "}
                      download{row.count === 1 ? "" : "s"} · last{" "}
                      {fmtRelative(row.last_at)}
                      {row.last_admin_email && (
                        <span className="hidden sm:inline">
                          {" "}
                          by {row.last_admin_email.split("@")[0]}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href={mailto}
                    className="inline-flex items-center gap-1 text-xs font-semibold border border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-2.5 py-1 rounded-sm transition-colors"
                    data-testid={`flyer-leaderboard-followup-${idx}`}
                    title={`Open your email client with a pre-written follow-up to ${row.prospect}`}
                  >
                    <Mail className="w-3.5 h-3.5" /> Follow-up
                  </a>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const res = await api.get(
                          `/admin/sales-flyer.pdf?prospect=${encodeURIComponent(
                            row.prospect
                          )}`,
                          { responseType: "blob" }
                        );
                        const url = window.URL.createObjectURL(
                          new Blob([res.data], { type: "application/pdf" })
                        );
                        const a = document.createElement("a");
                        a.href = url;
                        const safe = row.prospect
                          .replace(/[^a-z0-9-_ ]/gi, "")
                          .trim()
                          .replace(/\s+/g, "-")
                          .slice(0, 40);
                        a.download = `RealtyVault-Sales-Flyer-${safe}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        window.URL.revokeObjectURL(url);
                        // bump the count after a successful download
                        setTimeout(load, 600);
                      } catch {
                        /* silent — keep UI calm */
                      }
                    }}
                    className="inline-flex items-center gap-1 text-xs font-semibold border border-slate-300 hover:border-slate-900 text-slate-700 hover:text-slate-900 px-2.5 py-1 rounded-sm transition-colors"
                    data-testid={`flyer-leaderboard-redownload-${idx}`}
                    title="Re-download a fresh personalized flyer"
                  >
                    <FileDown className="w-3.5 h-3.5" /> Re-send
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="text-sm text-slate-400 italic py-4">
          {loading
            ? "Loading…"
            : "No personalized flyers yet — try downloading one with a prospect name to start tracking."}
        </div>
      )}
    </div>
  );
}
